import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { patchOnboardingStateOnServer, type OnboardingState } from '../../../../utils/net/onboardingApi';
import useMapModalStore from '../../../../stores/useMapModalStore';
import useMapOnboardingStore from '../../../../stores/useMapOnboardingStore';
import MapOnboardingModal from '../../../../ui/modals/map/MapOnboardingModal';

/** Map onboarding tutorial modal (`activeModal === 'mapOnboarding'`). Self-fetches queryClient. */
export default function MapOnboardingHost() {
  const queryClient = useQueryClient();
  const isMapOnboardingModalOpen = useMapModalStore((s) => s.activeModal === 'mapOnboarding');
  const closeModal = useMapModalStore((s) => s.closeModal);
  const setOnboardingStep = useMapOnboardingStore((s) => s.setOnboardingStep);
  const setIsOnboardingBuildHousePopupOpen = useMapOnboardingStore((s) => s.setIsOnboardingBuildHousePopupOpen);

  return (
        <MapOnboardingModal
          isOpen={isMapOnboardingModalOpen}
          onClose={() => {
            closeModal();
            setOnboardingStep(0);
            const currentMonth = new Date().getFullYear() + "_" + String(new Date().getMonth() + 1).padStart(2, "0");
            // Optimistically mark the map tutorial as done locally so the nav
            // blocker releases immediately. Without this, the parallel
            // `invalidateQueries` below can race the PATCH: the GET may reach
            // the server before the PATCH is persisted, returning stale data
            // (no `map_onboarding_month`) → cache gets stale → the
            // `!mapTutorialConfirmedDone` fail-closed guard keeps the nav
            // blocked forever after the user finishes onboarding.
            queryClient.setQueryData<OnboardingState>(['onboarding-state'], (old) =>
              old ? { ...old, map_onboarding_month: currentMonth } : old
            );
            patchOnboardingStateOnServer({ map_onboarding_month: currentMonth })
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ['onboarding-state'] }).catch(() => {});
              })
              .catch(() => {});
            posthog.capture('onboarding_map_tutorial_completed');
            posthog.capture('onboarding_build_house_popup_shown');
            setIsOnboardingBuildHousePopupOpen(true);
          }}
          onStepChange={(step) => {
            setOnboardingStep(step);
          }}
        />
  );
}
